import { useCallback, useEffect, useRef, useState } from "react";
import { useCellSaveState } from "./useCellSaveState";

/**
 * Coordinates inline edits for an entire row.
 *
 * Saves for different fields are serialized by row, while pending values stay
 * visible. Each save is built from the last confirmed row so a failed request
 * cannot be accidentally included in the next field update.
 */
export function useQueuedRowEdits({
  rows = [],
  keyPrefix,
  getRowId = (row) => row.id,
  prepareRow = (row) => row,
  onSave,
}) {
  const [displayRows, setDisplayRows] = useState(rows);
  const confirmedRows = useRef(new Map());
  const pendingEdits = useRef(new Map());
  const nextToken = useRef(0);
  const mounted = useRef(true);
  const getRowIdRef = useRef(getRowId);
  const prepareRowRef = useRef(prepareRow);
  const onSaveRef = useRef(onSave);
  const { getSaveStatus, saveCell } = useCellSaveState();

  getRowIdRef.current = getRowId;
  prepareRowRef.current = prepareRow;
  onSaveRef.current = onSave;

  const applyPendingEdits = useCallback((row, rowId) => {
    const edits = pendingEdits.current.get(rowId);
    if (!edits) return prepareRowRef.current(row);

    const updatedRow = { ...row };
    edits.forEach(({ value }, field) => {
      updatedRow[field] = value;
    });

    return prepareRowRef.current(updatedRow);
  }, []);

  const refreshDisplayedRow = useCallback(
    (rowId, fallbackRow) => {
      if (!mounted.current) return;

      setDisplayRows((currentRows) =>
        currentRows.map((currentRow) => {
          if (getRowIdRef.current(currentRow) !== rowId) return currentRow;

          const confirmedRow = confirmedRows.current.get(rowId) ?? fallbackRow;
          return applyPendingEdits(confirmedRow, rowId);
        }),
      );
    },
    [applyPendingEdits],
  );

  useEffect(() => {
    mounted.current = true;
    const activeIds = new Set();

    rows.forEach((row) => {
      const rowId = getRowIdRef.current(row);
      activeIds.add(rowId);

      // A parent refresh can finish with an older row while this row still has
      // queued work. Keep the last locally confirmed snapshot until the queue
      // becomes idle so the next field save cannot reintroduce stale values.
      if (!pendingEdits.current.has(rowId)) {
        confirmedRows.current.set(rowId, row);
      }
    });

    confirmedRows.current.forEach((_, rowId) => {
      if (!activeIds.has(rowId) && !pendingEdits.current.has(rowId)) {
        confirmedRows.current.delete(rowId);
      }
    });

    setDisplayRows(
      rows.map((row) => {
        const rowId = getRowIdRef.current(row);
        const baseRow = pendingEdits.current.has(rowId)
          ? confirmedRows.current.get(rowId) ?? row
          : row;
        return applyPendingEdits(baseRow, rowId);
      }),
    );

    return () => {
      mounted.current = false;
    };
  }, [applyPendingEdits, rows]);

  const saveField = useCallback(
    (row, field, value, context) => {
      const rowId = getRowIdRef.current(row);
      const token = ++nextToken.current;
      const rowEdits = pendingEdits.current.get(rowId) ?? new Map();
      rowEdits.set(field, { token, value });
      pendingEdits.current.set(rowId, rowEdits);
      refreshDisplayedRow(rowId, row);

      const cellKey = `${keyPrefix}:${rowId}:${field}`;
      const rowQueueKey = `${keyPrefix}:${rowId}`;

      return saveCell(
        cellKey,
        async () => {
          const confirmedRow = confirmedRows.current.get(rowId) ?? row;
          const updatedRow = prepareRowRef.current({
            ...confirmedRow,
            [field]: value,
          });
          let result;

          try {
            result = await onSaveRef.current(updatedRow, context);
            if (result?.success) {
              confirmedRows.current.set(rowId, updatedRow);
            }
          } catch (error) {
            result = { success: false, error };
          } finally {
            const currentEdits = pendingEdits.current.get(rowId);
            if (currentEdits?.get(field)?.token === token) {
              currentEdits.delete(field);
              if (currentEdits.size === 0) {
                pendingEdits.current.delete(rowId);
              }
            }
            refreshDisplayedRow(rowId, row);
          }

          return result;
        },
        rowQueueKey,
      );
    },
    [keyPrefix, refreshDisplayedRow, saveCell],
  );

  const getFieldSaveStatus = useCallback(
    (row, field) => getSaveStatus(`${keyPrefix}:${getRowIdRef.current(row)}:${field}`),
    [getSaveStatus, keyPrefix],
  );

  return { rows: displayRows, getFieldSaveStatus, saveField };
}
